import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Calendar, MapPin, Store, Video, Phone, Truck, Clock, AlertTriangle, Lightbulb } from 'lucide-react';
import AppointmentCalendar from '../components/AppointmentCalendar';
import AppointmentForm from '../components/AppointmentForm';
import { fetchTypes } from '../lib/appointments';
import SEO from '../components/SEO';
import { SEO_TEXTES } from '../data/seoTextes';
import { useLang, t, getLocale, i18nField } from '../context/LanguageContext';
import translations from '../i18n/translations';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

// Phase 4.0 (2026-05-22) : icone par delivery_mode au lieu d'un mapping
// hardcode par alias. Plus extensible — chaque nouveau type heritera
// automatiquement de l'icone correcte selon son mode.
const DELIVERY_MODE_ICONS = {
  onsite_workshop: Store,
  remote_phone: Phone,
  visio_teams: Video,
  onsite_client: Truck,
};

// Labels traduits via i18n (Phase 4.3, 2026-05-22). Le composant resout le
// label en runtime via `t(DELIVERY_MODE_LABEL_KEYS[mode], lang)`.
const DELIVERY_MODE_LABEL_KEYS = {
  onsite_workshop: 'mode_onsite_workshop',
  remote_phone: 'mode_remote_phone',
  visio_teams: 'mode_visio_teams',
  onsite_client: 'mode_onsite_client',
};

function iconForType(typ) {
  return DELIVERY_MODE_ICONS[typ?.delivery_mode] || Store;
}

// Construit le libellé prix affiché sur la carte type et dans le récap.
function formatTypePrice(typ, lang) {
  if (!typ) return null;
  const override = i18nField(typ, 'price_label_override', lang);
  if (override) return { label: override, refundable: false };
  if (typeof typ.indicative_price_eur_ttc === 'number' && typ.indicative_price_eur_ttc > 0) {
    const prefix = t(translations.rdv.price_from, lang);
    const suffix = lang === 'fr' ? ' € TTC' : ' € incl. VAT';
    return {
      label: `${prefix} ${typ.indicative_price_eur_ttc}${suffix}`,
      refundable: !!typ.price_refundable_on_intervention,
    };
  }
  return null;
}

export default function PrendreRdv() {
  const { lang } = useLang();
  const tr = translations.rdv;
  const [step, setStep] = useState('loading');
  const [availableTypes, setAvailableTypes] = useState([]);
  const [typeAlias, setTypeAlias] = useState(null);
  const [durationMin, setDurationMin] = useState(null); // Phase 4.1 : custom duration
  const [slot, setSlot] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [typesError, setTypesError] = useState(null);
  // Phase 5 (2026-05-22) : query param `?type=alias` precharge le type (utilise
  // par le configurateur pour rediriger vers le picker durée).
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get('type');

  useEffect(() => {
    let cancelled = false;
    fetchTypes()
      .then((data) => {
        if (cancelled) return;
        const types = Array.isArray(data?.types) ? data.types : [];
        setAvailableTypes(types);
        if (types.length === 0) {
          setTypesError(t(tr.bookings_disabled, lang));
          setStep('error');
          return;
        }
        // Phase 5 : si ?type= demande un type precis (configurateur redirect),
        // on saute a la suite du parcours.
        const preselected = requestedType
          ? types.find((x) => x.alias === requestedType)
          : null;
        if (preselected) {
          setTypeAlias(preselected.alias);
          if (preselected.prerequisite_type_alias) {
            setStep('prerequisite-warning');
          } else if (preselected.allow_custom_duration) {
            setStep('select-duration');
          } else {
            setStep('select-slot');
          }
        } else if (types.length === 1) {
          setTypeAlias(types[0].alias);
          setStep('select-slot');
        } else {
          setStep('select-type');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setTypesError(t(tr.error_loading_types, lang));
        setStep('error');
      });
    return () => {
      cancelled = true;
    };
  }, [lang, tr.bookings_disabled, tr.error_loading_types, requestedType]);

  const handlePickType = (alias) => {
    setTypeAlias(alias);
    setDurationMin(null); // reset si on rechange de type
    const picked = availableTypes.find((x) => x.alias === alias);
    // Si le type sélectionné déclare un prérequis (ex: diagnostic_w11),
    // on affiche un écran intermédiaire d'avertissement avant le calendrier.
    if (picked && picked.prerequisite_type_alias) {
      setStep('prerequisite-warning');
    } else if (picked && picked.allow_custom_duration) {
      // Phase 4.1 : type a durée libre (ex: personnalise) — demander la durée
      // au client avant de chercher des créneaux.
      setStep('select-duration');
    } else {
      setStep('select-slot');
    }
  };
  const handlePickDuration = (mins) => {
    setDurationMin(mins);
    setStep('select-slot');
  };
  const handleSkipPrerequisite = () => {
    setStep('select-slot');
  };
  const handleSwitchToPrerequisite = () => {
    const current = availableTypes.find((x) => x.alias === typeAlias);
    const reqAlias = current?.prerequisite_type_alias;
    if (!reqAlias) return;
    const found = availableTypes.find((x) => x.alias === reqAlias);
    if (!found) return;
    setTypeAlias(reqAlias);
    setStep('select-slot');
  };
  const handleSelectSlot = (s) => {
    setSlot(s);
    setStep('form');
  };
  const handleSuccess = (data) => {
    setConfirmation(data);
    setStep('done');
  };

  const currentType = availableTypes.find((typ) => typ.alias === typeAlias) || null;
  const canChangeType = availableTypes.length >= 2;
  // Phase 4.0 : message de confirmation dirigé par delivery_mode.
  const deliveryMode = currentType?.delivery_mode || 'onsite_workshop';

  return (
    <>
      <SEO {...SEO_TEXTES['/prendre-rdv']} path="/prendre-rdv" />
    <main data-testid="prendre-rdv-page">
      {/* CTA : redirige les visiteurs indecis vers le configurateur guide */}
      <div
        className="bg-amber-500/10 border-b border-amber-500/30 backdrop-blur-sm px-4 py-3 mt-[calc(var(--persona-h,72px)+80px)]"
        data-testid="configurator-cta-banner"
      >
        <div className="max-w-3xl mx-auto text-sm text-amber-200 flex items-center gap-3">
          <Lightbulb size={18} className="flex-shrink-0" />
          <span>
            {t(tr.indecisive_intro, lang)}{' '}
            <Link
              to="/configurateur"
              className="font-semibold underline hover:no-underline text-amber-100"
              data-testid="configurator-cta-link"
            >
              {t(tr.indecisive_cta, lang)}
            </Link>
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="pt-12 pb-12">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">
              {t(tr.eyebrow, lang)}
            </p>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
              {t(tr.title, lang)}
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-3">
              {t(tr.subtitle, lang)}
            </p>
            <p className="text-sm text-slate-400 leading-relaxed">
              {t(tr.description, lang)}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0099D6]/10 border border-[#0099D6]/20 text-[#0099D6]">
                <MapPin className="w-3.5 h-3.5" />
                {t(tr.workshop_address, lang)}
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300">
                {t(tr.walk_in, lang)}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Card RDV */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            {...fadeUp}
            className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-6 md:p-10"
          >
            {step === 'loading' && (
              <p
                className="text-center py-8 text-slate-400 text-sm"
                data-testid="rdv-loading"
              >
                {t(tr.loading, lang)}
              </p>
            )}

            {step === 'error' && (
              <div
                className="text-center py-8"
                data-testid="rdv-types-error"
              >
                <p className="text-red-300 bg-red-500/10 border border-red-400/30 p-4 rounded-lg text-sm font-medium">
                  {typesError || t(tr.bookings_unavailable, lang)}
                </p>
              </div>
            )}

            {step === 'select-type' && (
              <div data-testid="type-selector">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-5 h-5 text-[#0099D6]" />
                  <h2 className="text-lg font-semibold text-white">
                    {t(tr.select_type_title, lang)}
                  </h2>
                </div>
                <p className="text-slate-400 text-sm mb-6">
                  {t(tr.select_type_subtitle, lang)}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {availableTypes.map((typ) => {
                    const Icon = iconForType(typ);
                    const price = formatTypePrice(typ, lang);
                    const modeKey = DELIVERY_MODE_LABEL_KEYS[typ.delivery_mode];
                    const modeLabel = modeKey ? t(tr[modeKey], lang) : null;
                    return (
                      <button
                        key={typ.alias}
                        type="button"
                        onClick={() => handlePickType(typ.alias)}
                        className="group flex flex-col items-start gap-3 p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-[#0099D6]/60 hover:bg-[#0099D6]/5 backdrop-blur-sm transition-all text-left active:scale-[0.98] hover:shadow-[0_0_20px_rgba(0,153,214,0.25)]"
                        data-testid={`type-btn-${typ.alias}`}
                      >
                        <div className="w-12 h-12 rounded-xl bg-[#0099D6]/10 group-hover:bg-[#0099D6] text-[#0099D6] group-hover:text-white flex items-center justify-center transition-colors">
                          <Icon size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-white">
                            {i18nField(typ, 'label', lang)}
                          </h3>
                          {modeLabel && (
                            <span
                              className="inline-block mt-1 text-xs font-semibold text-[#0099D6] bg-[#0099D6]/10 px-2 py-0.5 rounded"
                              data-testid={`type-mode-${typ.alias}`}
                            >
                              {modeLabel}
                            </span>
                          )}
                          {i18nField(typ, 'description', lang) && (
                            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                              {i18nField(typ, 'description', lang)}
                            </p>
                          )}
                        </div>
                        <div className="mt-auto flex flex-col gap-1 w-full">
                          {price && (
                            <div data-testid={`type-price-${typ.alias}`}>
                              <span className="inline-block text-sm font-semibold text-[#0099D6]">
                                {price.label}
                              </span>
                              {price.refundable && (
                                <p className="text-xs text-slate-400 mt-0.5 italic">
                                  {t(tr.price_refundable, lang)}
                                </p>
                              )}
                            </div>
                          )}
                          {typ.allow_custom_duration && Array.isArray(typ.custom_duration_options) && typ.custom_duration_options.length > 0 ? (
                            <span className="text-xs font-semibold text-[#0099D6] uppercase tracking-wider">
                              {t(tr.duration_at_choice, lang)} {typ.custom_duration_options.join(' / ')} {t(tr.minutes_abbr, lang)}
                            </span>
                          ) : typeof typ.duration_min === 'number' ? (
                            <span className="text-xs font-semibold text-[#0099D6] uppercase tracking-wider">
                              {typ.duration_min} {t(tr.minutes_abbr, lang)}
                            </span>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 'prerequisite-warning' && currentType && (
              <div data-testid="prerequisite-warning">
                {canChangeType && (
                  <button
                    type="button"
                    onClick={() => setStep('select-type')}
                    className="flex items-center gap-2 mb-6 text-[#0099D6] hover:underline text-sm font-medium"
                    data-testid="back-to-type"
                  >
                    <ArrowLeft size={16} />
                    {t(tr.change_type, lang)}
                  </button>
                )}
                <div className="flex items-start gap-3 mb-6 p-4 rounded-xl bg-amber-500/5 border border-amber-400/30">
                  <AlertTriangle className="text-amber-300 shrink-0 mt-0.5" size={22} />
                  <div>
                    <h2 className="text-xl font-bold text-white mb-1">
                      {t(tr.prereq_title, lang)}
                    </h2>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {t(tr.prereq_text_before, lang)}{' '}
                      <strong className="text-white">{i18nField(currentType, 'label', lang)}</strong>{' '}
                      {t(tr.prereq_text_middle, lang)}{' '}
                      <strong className="text-[#0099D6]">
                        «{' '}
                        {(() => {
                          const req = availableTypes.find(
                            (x) => x.alias === currentType.prerequisite_type_alias,
                          );
                          return i18nField(req, 'label', lang) || currentType.prerequisite_type_alias;
                        })()}{' '}
                        »
                      </strong>
                      .
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={handleSwitchToPrerequisite}
                    className="bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-3 px-6 rounded-lg flex-1 transition-all hover:scale-[1.02] active:scale-95 shadow-[0_0_20px_rgba(0,153,214,0.25)] hover:shadow-[0_0_30px_rgba(0,153,214,0.4)]"
                    data-testid="prereq-take-diagnostic"
                  >
                    {t(tr.prereq_take_diagnostic, lang)}
                  </button>
                  <button
                    type="button"
                    onClick={handleSkipPrerequisite}
                    className="bg-white/5 hover:bg-white/10 text-slate-200 font-semibold py-3 px-6 rounded-lg border border-white/10 flex-1 transition-all active:scale-95"
                    data-testid="prereq-skip"
                  >
                    {t(tr.prereq_skip, lang)}
                  </button>
                </div>
              </div>
            )}

            {step === 'select-duration' && currentType?.allow_custom_duration && (
              <div data-testid="duration-selector">
                <button
                  type="button"
                  onClick={() => setStep('select-type')}
                  className="flex items-center gap-2 mb-6 text-[#0099D6] hover:underline text-sm font-medium"
                  data-testid="back-to-type-from-duration"
                >
                  <ArrowLeft size={16} />
                  {t(tr.change_type, lang)}
                </button>
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-5 h-5 text-[#0099D6]" />
                  <h2 className="text-lg font-semibold text-white">
                    {t(tr.duration_title, lang)}
                  </h2>
                </div>
                <p className="text-slate-400 text-sm mb-6">
                  {t(tr.duration_subtitle_prefix, lang)} <strong className="text-white">{i18nField(currentType, 'label', lang)}</strong> {t(tr.duration_subtitle_suffix, lang)}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {(currentType.custom_duration_options || [15, 30, 60]).map((mins) => (
                    <button
                      key={mins}
                      type="button"
                      onClick={() => handlePickDuration(mins)}
                      className="flex flex-col items-center gap-2 p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-[#0099D6]/60 hover:bg-[#0099D6]/5 backdrop-blur-sm transition-all active:scale-[0.98] hover:shadow-[0_0_20px_rgba(0,153,214,0.25)]"
                      data-testid={`duration-btn-${mins}`}
                    >
                      <span className="font-bold text-3xl text-white">{mins}</span>
                      <span className="text-xs font-semibold text-[#0099D6] uppercase tracking-wider">{t(tr.minutes_abbr, lang)}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 italic mt-4 text-center">
                  {t(tr.duration_hint, lang)}
                </p>
              </div>
            )}

            {step === 'select-slot' && typeAlias && (
              <>
                <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-[#0099D6]" />
                    <h2 className="text-lg font-semibold text-white">
                      {t(tr.choose_slot, lang)}
                    </h2>
                  </div>
                  {canChangeType && (
                    <button
                      type="button"
                      onClick={() => setStep(currentType?.allow_custom_duration ? 'select-duration' : 'select-type')}
                      className="flex items-center gap-2 text-[#0099D6] hover:underline text-sm font-medium"
                      data-testid="back-to-type"
                    >
                      <ArrowLeft size={16} />
                      {currentType?.allow_custom_duration ? t(tr.change_duration, lang) : t(tr.change_type, lang)}
                    </button>
                  )}
                </div>
                {currentType && (
                  <div className="mb-6 flex items-center gap-3 p-3 bg-white/[0.02] border border-white/10 rounded-lg">
                    <div className="w-9 h-9 rounded-lg bg-[#0099D6]/10 text-[#0099D6] flex items-center justify-center">
                      {(() => {
                        const Icon = iconForType(currentType);
                        return <Icon size={18} />;
                      })()}
                    </div>
                    <div className="text-sm text-slate-300">
                      <strong className="text-white">{i18nField(currentType, 'label', lang)}</strong>
                      {durationMin ? (
                        <span className="text-slate-400"> · {durationMin} {t(tr.minutes_abbr, lang)}</span>
                      ) : typeof currentType.duration_min === 'number' ? (
                        <span className="text-slate-400"> · {currentType.duration_min} {t(tr.minutes_abbr, lang)}</span>
                      ) : null}
                      {DELIVERY_MODE_LABEL_KEYS[currentType.delivery_mode] && (
                        <span className="text-slate-400"> · {t(tr[DELIVERY_MODE_LABEL_KEYS[currentType.delivery_mode]], lang)}</span>
                      )}
                      {(() => {
                        const price = formatTypePrice(currentType, lang);
                        if (!price) return null;
                        return <span className="text-slate-400"> · {price.label}</span>;
                      })()}
                    </div>
                  </div>
                )}
                {i18nField(currentType, 'important_notice', lang) && (
                  <div
                    className="mb-6 bg-amber-500/10 border border-amber-500/30 backdrop-blur-sm rounded-lg p-4"
                    data-testid="important-notice-banner"
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        size={20}
                        className="text-amber-300 flex-shrink-0 mt-0.5"
                      />
                      <p className="text-sm text-amber-200 leading-relaxed">
                        {i18nField(currentType, 'important_notice', lang)}
                      </p>
                    </div>
                  </div>
                )}
                <AppointmentCalendar
                  onSelectSlot={handleSelectSlot}
                  typeAlias={typeAlias}
                  durationMin={durationMin}
                />
              </>
            )}

            {step === 'form' && (
              <>
                <button
                  type="button"
                  onClick={() => setStep('select-slot')}
                  className="flex items-center gap-2 mb-6 text-[#0099D6] hover:underline text-sm font-medium"
                  data-testid="back-to-cal"
                >
                  <ArrowLeft size={16} />
                  {t(tr.change_slot, lang)}
                </button>
                <AppointmentForm
                  slot={slot}
                  typeAlias={typeAlias}
                  durationMin={durationMin}
                  importantNotice={i18nField(currentType, 'important_notice', lang) || null}
                  onSuccess={handleSuccess}
                />
              </>
            )}

            {step === 'done' && confirmation && (
              <div className="text-center py-8" data-testid="appointment-confirmed">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  {t(tr.appointment_confirmed, lang)}
                </h2>
                <p className="text-slate-300 mb-2">
                  {t(tr.slot_intro, lang)}{' '}
                  <strong className="text-[#0099D6]">
                    {new Date(slot.start).toLocaleString(getLocale(lang), {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </strong>{' '}
                  {t(tr.slot_validated, lang)}
                </p>
                <p className="text-slate-400 text-sm">
                  {/* Phase 4.0 : message dirigé par delivery_mode. Phase 4.3 : i18n. */}
                  {deliveryMode === 'visio_teams' && t(tr.email_visio, lang)}
                  {deliveryMode === 'remote_phone' && t(tr.email_remote_phone, lang)}
                  {deliveryMode === 'onsite_client' && t(tr.email_onsite_client, lang)}
                  {(deliveryMode === 'onsite_workshop' || !deliveryMode) && t(tr.email_comptoir, lang)}
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </main>
    </>
  );
}
